import React, { useState, useEffect, useRef } from "react";
import { Icon } from "@iconify/react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./audio.css";

const AuthenticatedAudio = ({ url, token, onPlay }) => {
    const [audioSrc, setAudioSrc] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    
    // Player State
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [gain, setGain] = useState(1); // 0 to 3
    const [playbackRate, setPlaybackRate] = useState(1);
    const [showSpeedMenu, setShowSpeedMenu] = useState(false);
    
    const audioRef = useRef(null);
    const audioContextRef = useRef(null);
    const sourceRef = useRef(null);
    const gainNodeRef = useRef(null);

    useEffect(() => {
        return () => {
            if (audioSrc) URL.revokeObjectURL(audioSrc);
            if (audioContextRef.current) {
                audioContextRef.current.close();
                audioContextRef.current = null;
            }
        };
    }, [audioSrc]);

    const loadAudio = async () => {
        if (audioSrc) return;
        setLoading(true);
        try {
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error(response.statusText);
            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            setAudioSrc(objectUrl);
        } catch (err) {
            console.error("Error loading audio", err);
            setError(true);
            toast.error("Erro ao carregar áudio");
        } finally {
            setLoading(false);
        }
    };

    const initAudioContext = () => {
        if (!audioRef.current || audioContextRef.current) return;

        try {
            const AudioContext = window.AudioContext || window.webkitAudioContext;
            const ctx = new AudioContext();
            audioContextRef.current = ctx;

            const source = ctx.createMediaElementSource(audioRef.current);
            sourceRef.current = source;

            // Compressor to prevent clipping
            const compressor = ctx.createDynamicsCompressor();
            compressor.threshold.setValueAtTime(-24, ctx.currentTime);
            compressor.knee.setValueAtTime(30, ctx.currentTime);
            compressor.ratio.setValueAtTime(12, ctx.currentTime);
            compressor.attack.setValueAtTime(0.003, ctx.currentTime);
            compressor.release.setValueAtTime(0.25, ctx.currentTime);

            const gainNode = ctx.createGain();
            gainNode.gain.value = gain;
            gainNodeRef.current = gainNode;

            source.connect(compressor);
            compressor.connect(gainNode);
            gainNode.connect(ctx.destination);
        } catch (e) {
            console.error("Audio Context Init Error", e);
        }
    };

    useEffect(() => {
        if (audioSrc && audioRef.current) {
            initAudioContext();
        }
    }, [audioSrc]);

    // Handle Volume/Gain Change
    useEffect(() => {
        if (gainNodeRef.current) {
            gainNodeRef.current.gain.value = gain;
        }
    }, [gain]);

    // Handle Playback Rate Change
    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.playbackRate = playbackRate;
        }
    }, [playbackRate]);

    // Audio Event Handlers
    const togglePlay = () => {
        if (!audioRef.current) return;
        
        // Ensure context is running
        if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
            audioContextRef.current.resume();
        }

        if (isPlaying) {
            audioRef.current.pause();
        } else {
            audioRef.current.play();
            if (onPlay) onPlay();
        }
        setIsPlaying(!isPlaying);
    };

    const handleTimeUpdate = () => {
        if (audioRef.current) {
            setCurrentTime(audioRef.current.currentTime);
        }
    };

    const handleLoadedMetadata = () => {
        if (audioRef.current) {
            setDuration(audioRef.current.duration);
            // Auto play logic if desired, or let user click play
            // Since we load on "Reproduzir" click, we might want to auto-play
            audioRef.current.play().then(() => {
                setIsPlaying(true);
                if (onPlay) onPlay();
            }).catch(() => setIsPlaying(false));
        }
    };

    const handleEnded = () => {
        setIsPlaying(false);
        setCurrentTime(0);
        if (audioRef.current) audioRef.current.currentTime = 0;
    };

    const handleSeek = (e) => {
        const time = parseFloat(e.target.value);
        if (audioRef.current) {
            audioRef.current.currentTime = time;
            setCurrentTime(time);
        }
    };

    const formatTime = (time) => {
        if (isNaN(time)) return "0:00";
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    if (error) return <div className="text-danger text-center small p-2">Erro ao carregar</div>;

    if (audioSrc) {
        return (
            <div className="w-100">
                <audio 
                    ref={audioRef}
                    src={audioSrc}
                    onTimeUpdate={handleTimeUpdate}
                    onLoadedMetadata={handleLoadedMetadata}
                    onEnded={handleEnded}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    controlsList="nodownload"
                    className="d-none" 
                />
                
                {/* Responsive Compact Player UI */}
                <div className="d-flex flex-wrap flex-md-nowrap align-items-center gap-3 w-100">
                    
                    {/* Play/Pause Button */}
                    <button 
                        className="btn btn-primary btn-sm rounded-circle p-0 d-flex align-items-center justify-content-center flex-shrink-0"
                        style={{ width: '40px', height: '40px' }}
                        onClick={togglePlay}
                    >
                        <Icon icon={isPlaying ? "solar:pause-bold" : "solar:play-bold"} width="22" />
                    </button>
                    
                    {/* Progress Bar & Time */}
                    <div className="d-flex align-items-center gap-2 flex-grow-1 w-100 order-3 order-md-1">
                        <span className="text-xs text-secondary-light fw-medium" style={{ minWidth: '35px', textAlign: 'center' }}>
                            {formatTime(currentTime)}
                        </span>
                        <input 
                            type="range" 
                            min="0" 
                            max={duration || 100} 
                            value={currentTime} 
                            onChange={handleSeek}
                            className="form-range flex-grow-1 cursor-pointer"
                            style={{ height: '4px' }}
                        />
                        <span className="text-xs text-secondary-light fw-medium" style={{ minWidth: '35px', textAlign: 'center' }}>
                            {formatTime(duration)}
                        </span>
                    </div>

                    {/* Volume/Boost Control */}
                    <div className="d-flex align-items-center gap-2 flex-shrink-0 order-1 order-md-2 ms-auto ms-md-0">
                        <div className="d-flex align-items-center gap-2 bg-base border border-neutral-200 rounded-pill px-3 py-1" style={{ height: '32px' }}>
                            <Icon 
                                icon={gain === 0 ? "solar:volume-cross-bold" : (gain > 1 ? "solar:volume-loud-bold" : "solar:volume-small-bold")} 
                                className={`flex-shrink-0 ${gain > 1 ? 'text-primary-600' : 'text-secondary-light'}`}
                                width="18" 
                            />
                            <input 
                                type="range" 
                                min="0" 
                                max="3" 
                                step="0.1" 
                                value={gain} 
                                onChange={(e) => setGain(parseFloat(e.target.value))}
                                className="form-range cursor-pointer"
                                style={{ width: '60px', height: '4px' }}
                                title="Volume e Boost (até 300%)"
                            />
                            <span className={`text-xs fw-bold ${gain > 1 ? 'text-primary-600' : 'text-secondary-light'}`} style={{ minWidth: '36px', textAlign: 'right' }}>
                                {Math.round(gain * 100)}%
                            </span>
                        </div>
                    </div>

                    {/* Playback Speed Control */}
                    <div className="position-relative flex-shrink-0 order-2 order-md-3">
                        <button 
                            className="d-flex align-items-center justify-content-between gap-1 bg-base border border-neutral-200 text-secondary-light fw-bold cursor-pointer rounded-pill px-3"
                            style={{ height: '32px', fontSize: '0.8rem', minWidth: '75px' }}
                            onClick={() => setShowSpeedMenu(!showSpeedMenu)}
                            onBlur={() => setShowSpeedMenu(false)}
                        >
                            <span>{playbackRate}x</span>
                            <Icon icon="solar:alt-arrow-down-bold" width="12" />
                        </button>
                        
                        {showSpeedMenu && (
                            <div 
                                className="position-absolute top-100 end-0 mt-1 bg-base border border-neutral-200 rounded-3 shadow-sm overflow-hidden"
                                style={{ zIndex: 10, minWidth: '100%' }}
                            >
                                {[0.5, 0.75, 1, 1.25, 1.5, 2].map(rate => (
                                    <button
                                        key={rate}
                                        className={`d-block w-100 text-start px-3 py-2 border-0 bg-transparent ${playbackRate === rate ? 'text-primary-600 fw-bold bg-primary-50' : 'text-secondary-light hover-bg-neutral-50'}`}
                                        style={{ fontSize: '0.8rem' }}
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            setPlaybackRate(rate);
                                            setShowSpeedMenu(false);
                                        }}
                                    >
                                        {rate}x
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="d-flex align-items-center justify-content-center w-100" style={{ minHeight: '54px' }}>
            {loading ? (
                <div className="spinner-border text-primary spinner-border-sm" role="status">
                    <span className="visually-hidden">Carregando...</span>
                </div>
            ) : (
                <button 
                    className="btn btn-primary btn-sm rounded-pill px-3 d-flex align-items-center gap-2" 
                    onClick={loadAudio}
                >
                    <Icon icon="solar:play-bold" />
                    <span>Reproduzir</span>
                </button>
            )}
        </div>
    );
};

export default AuthenticatedAudio;
